import React, { createRef } from 'react';
import {
  Button,
  CircularProgress,
  Paper,
  Step,
  StepButton,
  Stepper,
  TableCell,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  STEPPER_HEADINGS,
  STEP_DRAW_MACHINE_INDEX,
  STEP_SET_WINNER_PRICE_INDEX,
  STEP_IDENTIFY_WINNER_INDEX,
  BACK,
  RAFFLE,
  DRAW_MACHINE,
  RAFFLE_CUSTOMER_FIELDS,
  RAFFLE_PAGES,
} from './constants';

import './ManagerRaffleStepper.css';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import CustomToolbar from '../global/CustomToolbar';

// icons
import SaveIcon from '@material-ui/icons/Save';
import {
  AddAPhoto,
  ArrowBack,
  ArrowForward,
  Camera,
  Clear,
} from '@material-ui/icons';
import { NEXT } from '../global/constants';
import DrawIcon from '@mui/icons-material/Draw';
import celebrationGif from './celebration.gif';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import { Layout } from '../global/Table/TablePageHelpers/LayoutObject';
import TablePage from '../global/Table/TablePage';
import debounce from 'lodash/debounce';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import CancelIcon from '@mui/icons-material/Cancel';
import FlipCameraIosIcon from '@mui/icons-material/FlipCameraIos';
import bingo from './bingo.mp4';

// Customer search runs as the employee types rather than on blur. The wait is
// long enough that a normal typing burst costs one request, short enough that
// the results feel like they arrive with the keystroke.
const SEARCH_DEBOUNCE_MS = 400;

// Below this a term matches too much of the customer base to be useful, and
// the server refuses it anyway (CustomerService.searchCustomer).
const SEARCH_MIN_CHARS = 2;

class ManageRaffleStepper extends React.Component {
  constructor(props) {
    super(props);
    const isIOSSafari = /iP(ad|hone|od).*Safari/i.test(navigator.userAgent);
    this.state = {
      activeStep: 0,
      isDrawing: false,
      winner: null,
      currentNumber: 0,
      amount: null,
      selectedCustomer: null,
      search: '',
      searching: false,
      isMatchPictureShow: false,
      requiredList: ['winner', 'amount'],
      capturedImageURL: null,
      newPhotos: [],
      isPhotoVerified: false,
      hoveredRows: {},
      clickedRows: {},
      showVideo: false,
      cameraFacingMode: 'environment',
      drawnNumbers: [],
      // mobile
      isMobile: window.matchMedia('(max-width: 800px)').matches || isIOSSafari,
      isCameraOpen: false,
    };
    this.timer = null;
    this.videoRef = createRef();
    this.canvasRef = createRef();
  }

  componentDidUpdate(prevProps) {
    // Results are in, so the field can stop showing the in-flight spinner.
    if (prevProps.raffleCustomers !== this.props.raffleCustomers) {
      this.setState({ searching: false });
    }
  }

  componentWillUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
    }

    // A search still inside its debounce window would otherwise fire into an
    // unmounted component.
    this.debouncedFetchSearch.cancel();

    if (this.videoRef.current && this.videoRef.current.srcObject) {
      const stream = this.videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
    }
  }

  // === Desktop webcam (plain video stream, no facingMode) ===
  startWebcam = async () => {
    // Check if the ref is available
    if (this.videoRef.current) {
      try {
        const existing = this.videoRef.current?.srcObject;
        if (existing) {
          existing.getTracks().forEach((track) => track.stop());
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        // Ensure the videoRef.current is not null before setting srcObject
        if (this.videoRef.current) {
          this.videoRef.current.srcObject = stream;
          this.videoRef.current.play();
        }
      } catch (err) {
        console.error('Error accessing webcam: ', err);
      }
    } else {
      console.error('Video ref is not available');
    }
  };

  handleTakePhotoClick = () => {
    this.setState({ showVideo: true, capturedImageURL: null }, () => {
      this.startWebcam();
    });
  };

  stopWebcam = () => {
    const stream = this.videoRef.current?.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    this.setState({ showVideo: false });
  };

  // === Mobile camera (facingMode constraints + front/back switch) ===
  startCameraMobile = async () => {
    try {
      const existing = this.videoRef.current?.srcObject;
      if (existing) {
        existing.getTracks().forEach((track) => track.stop());
      }

      const constraints = {
        video: {
          facingMode: { ideal: this.state.cameraFacingMode },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      this.setState({ isCameraOpen: true });

      if (this.videoRef.current) {
        this.videoRef.current.srcObject = stream;
      }
    } catch (err) {
      alert('Camera access error: ' + err.message);
    }
  };

  switchCameraMobile = () => {
    const nextFacingMode =
      this.state.cameraFacingMode === 'environment' ? 'user' : 'environment';
    this.setState({ cameraFacingMode: nextFacingMode }, () => {
      if (this.state.isCameraOpen) {
        this.startCameraMobile();
      }
    });
  };

  stopCameraMobile = () => {
    const stream = this.videoRef.current?.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    this.setState({ isCameraOpen: false });
  };

  handleOpenCameraMobile = () => {
    if (!this.state.isCameraOpen) {
      this.startCameraMobile();
    }
  };

  capturePhotoMobile = () => {
    this.setState({ capturedImageURL: null, newPhotos: [] });

    const canvas = this.canvasRef.current;
    const video = this.videoRef.current;
    if (canvas && video) {
      const width = video.videoWidth;
      const height = video.videoHeight;
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, width, height);
      const compressedImageURL = canvas.toDataURL('image/jpeg', 0.5);
      this.setState({ capturedImageURL: compressedImageURL }, () => {
        this.stopCameraMobile();
      });

      this.handleFileAdd(compressedImageURL);
    }
  };

  getFormattedFileObject = (file) => {
    return {
      localURL: URL.createObjectURL(file),
      displayName: file.name,
      type: file.type,
      file,
    };
  };

  captureImage = () => {
    this.setState({ capturedImageURL: null, newPhotos: [] });

    const canvas = this.canvasRef.current;
    const video = this.videoRef.current;
    if (canvas && video) {
      const width = video.videoWidth;
      const height = video.videoHeight;
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, width, height);
      const compressedImageURL = canvas.toDataURL('image/jpeg', 0.5);
      this.setState({ capturedImageURL: compressedImageURL }, () => {
        this.stopWebcam();
      });

      // Save the captured image as a file in state.files
      this.handleFileAdd(compressedImageURL);
    }
  };

  handleFileAdd = async (fileDataURL) => {
    // Convert data URL to a file object
    fetch(fileDataURL)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File(
          [blob],
          `${Date.now() + Math.floor(Math.random() * 10000)}.png`,
          { type: blob.type }
        );
        const newFile = this.getFormattedFileObject(file);
        this.setState({
          newPhotos: newFile,
          capturedImageURL: newFile.localURL,
        });
      });
  };

  handleDraw = () => {
    this.setState({ isDrawing: true, winner: null });

    // Get already drawn machine numbers
    const drawnNumbers = this.state.drawnNumbers || [];

    // Filter machines that are not drawn yet
    const availableMachines = this.props.machines
      .map((m) => m.machine) // get the machine numbers as strings
      .filter((machine) => !drawnNumbers.includes(machine));

    if (availableMachines.length === 0) {
      this.props.openFeedbackBar('error', 'All machines have been drawn!');
      this.setState({ isDrawing: false });
      return;
    }

    // Pick a random machine from available ones
    const randomIndex = Math.floor(Math.random() * availableMachines.length);
    const randomMachine = availableMachines[randomIndex];

    // Countdown animation logic
    let count = 0;
    this.timer = setInterval(() => {
      // Show a random machine number during countdown
      const randomCountdownMachine =
        availableMachines[Math.floor(Math.random() * availableMachines.length)];
      this.setState({ currentNumber: randomCountdownMachine });

      count++;
      if (count > 10) {
        // stop after 10 iterations
        clearInterval(this.timer);

        // Save winner and update drawnNumbers
        this.setState((prevState) => ({
          winner: randomMachine,
          isDrawing: false,
          drawnNumbers: [...(prevState.drawnNumbers || []), randomMachine],
        }));
      }
    }, 500);
  };

  setStep = (index) => {
    this.setState({
      activeStep: index,
    });
  };

  fetchSearch = (term) => {
    this.setState({ searching: true });
    this.props.fetchSearch(term);
  };

  // The saga runs on takeLatest, so a slow response from an earlier keystroke
  // is dropped rather than overwriting the results for the current term.
  debouncedFetchSearch = debounce(this.fetchSearch, SEARCH_DEBOUNCE_MS);

  // Everything the search box resets when it no longer has a usable term.
  clearSearchResults = (extra = {}) => {
    // Drop the queued call as well, so a half-typed name cannot land a moment
    // after the employee emptied the field.
    this.debouncedFetchSearch.cancel();
    this.setState(
      {
        searching: false,
        isMatchPictureShow: false,
        selectedCustomer: null,
        capturedImageURL: null,
        newPhotos: [],
        isPhotoVerified: false,
        clickedRows: {},
        ...extra,
      },
      () => {
        this.props.clearAllCustomers();
      }
    );
  };

  handleSearchChange = (name, e) => {
    const { value } = e.target;
    const term = value.trim();
    this.setState({
      [name]: value,
    });

    if (term.length < SEARCH_MIN_CHARS) {
      this.clearSearchResults();
      return;
    }

    this.debouncedFetchSearch(term);
  };

  handleChange = (e) => {
    const { name, value } = e.target;

    const isPositiveInteger = /^[1-9]\d*$/.test(value);
    if (isPositiveInteger || value === '') {
      // Allow empty string for backspacing
      this.setState({
        [name]: value,
      });
    }
  };

  handleBack = () => {
    if (this.state.activeStep !== 0) {
      this.setState({
        activeStep: this.state.activeStep - 1,
      });
    }
  };

  handlePrevScreen = () => {
    this.props.setDisableToolBar(false);
    this.props.setCurrentPage(RAFFLE_PAGES.TABLE);
  };

  // Blur no longer searches - it only normalises what is left in the box.
  handleBlur = () => {
    const trimmed = this.state.search.trim();
    if (trimmed !== this.state.search) {
      this.setState({ search: trimmed });
    }
  };

  getStepContent = (stepIndex) => {
    switch (stepIndex) {
      case STEP_DRAW_MACHINE_INDEX:
        return this.firstStepContent();
      case STEP_SET_WINNER_PRICE_INDEX:
        return this.secondStepContent();
      case STEP_IDENTIFY_WINNER_INDEX:
        return this.thirdStepContent();
      default:
        return null;
    }
  };

  firstStepContent = () => {
    const { isDrawing, winner } = this.state;
    const containerStyle =
      winner !== null
        ? {
            backgroundImage: `url(${celebrationGif})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            animation: 'fadeInBackground 1s ease-in-out',
          }
        : {};

    return (
      <Paper elevation={0} className="raffle-step-card">
        <Typography className="raffle-step-heading">
          Step 1 &middot; Draw a Machine
        </Typography>
        <div className="stepper-page-one-grid">
          <div className="raffle-container" style={containerStyle}>
            {isDrawing ? (
              <div className="raffle-counting">
                <div className="video-container">
                  <video
                    className="lotto-video"
                    src={bingo}
                    autoPlay
                    loop
                    muted
                  />
                </div>
              </div>
            ) : winner !== null ? (
              <div className="raffle-winner">
                <h1 className="winner-text">{winner}</h1>
              </div>
            ) : (
              <div className="raffle-idle-hint">
                Select <strong>Draw Machine</strong> to pick a winning machine.
              </div>
            )}
          </div>
        </div>
      </Paper>
    );
  };

  secondStepContent = () => {
    return (
      <Paper elevation={0} className="raffle-step-card">
        <Typography className="raffle-step-heading">
          Step 2 &middot; Set Winner Amount
        </Typography>
        <div className="stepper-page-two-raffle-grid">
          <TextField
            className="input-text-field"
            variant="outlined"
            label="Winner Number"
            InputLabelProps={{ shrink: true }}
            name="winner"
            value={this.state.winner}
            onChange={this.handleChange}
            type="number"
            inputProps={{ min: '0', step: '1' }}
            required={this.state.requiredList.includes('winner')}
            fullWidth
          />

          <TextField
            className="input-text-field"
            variant="outlined"
            label="Amount"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            InputLabelProps={{ shrink: true }}
            name="amount"
            value={this.state.amount}
            onChange={this.handleChange}
            required={this.state.requiredList.includes('amount')}
            type="number"
            inputProps={{ min: '0', step: '5' }}
            fullWidth
          />
        </div>
      </Paper>
    );
  };

  thirdStepContent = () => {
    const {
      selectedCustomer,
      isMatchPictureShow,
      showVideo,
      capturedImageURL,
    } = this.state;

    return (
      <Paper elevation={0} className="raffle-step-card">
        <Typography className="raffle-step-heading">
          Step 3 &middot; Validate Customer
        </Typography>

        <div className="stepper-page-three-grid-raffle">
          <div className="raffle-search-wrap">
            <TextField
              className="input-text-field"
              margin="dense"
              id="outlined-basic"
              label="Search Customer"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment>
                    {this.state.searching && (
                      <CircularProgress size={16} thickness={5} />
                    )}
                    <IconButton
                      className="clear-button"
                      onClick={() => {
                        this.stopWebcam();
                        this.stopCameraMobile();
                        this.clearSearchResults({ search: '' });
                      }}
                    >
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              onChange={(e) => {
                this.handleSearchChange('search', e);
              }}
              onBlur={this.handleBlur}
              value={this.state.search}
            />
          </div>

          <canvas ref={this.canvasRef} style={{ display: 'none' }} />

          {this.props.raffleCustomers && this.state.search && (
            <div className="rowSpecSuppliers">
              {isMatchPictureShow && (
                <div className="raffle-verify-grid">
                  <div className="raffle-verify-panel">
                    <p className="raffle-verify-panel-label">
                      Customer on File
                    </p>
                    <div className="raffle-verify-media">
                      {selectedCustomer?.files?.[0]?.localURL ? (
                        <img
                          src={selectedCustomer.files[0].localURL}
                          alt="Customer"
                        />
                      ) : (
                        <span className="raffle-verify-placeholder">
                          No Image
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="raffle-verify-panel">
                    <p className="raffle-verify-panel-label">Live Capture</p>
                    <div className="raffle-verify-media">
                      {this.state.isMobile ? (
                        this.state.isCameraOpen ? (
                          <video
                            ref={this.videoRef}
                            autoPlay
                            playsInline
                            muted
                          />
                        ) : capturedImageURL ? (
                          <img src={capturedImageURL} alt="Captured" />
                        ) : (
                          <span className="raffle-verify-placeholder">
                            No Image
                          </span>
                        )
                      ) : showVideo ? (
                        <video ref={this.videoRef} />
                      ) : capturedImageURL ? (
                        <img src={capturedImageURL} alt="Captured" />
                      ) : (
                        <span className="raffle-verify-placeholder">
                          No Image
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!selectedCustomer && this.props.raffleCustomers.length > 0 && (
                <div style={{ width: '100%' }}>
                  <TablePage
                    tableName="CustomerTable"
                    tableRows={this.props.raffleCustomers}
                    tableColumnHeads={this.getTableHeaders()}
                    tableRow={this.tableRow}
                    paginationText="Results"
                    defaultSortBy="name"
                  />
                </div>
              )}

              {!selectedCustomer &&
                !this.state.searching &&
                this.state.search.trim().length >= SEARCH_MIN_CHARS &&
                this.props.raffleCustomers.length === 0 && (
                  <p className="raffle-search-empty">
                    No customer matches "{this.state.search.trim()}".
                  </p>
                )}
            </div>
          )}

          {isMatchPictureShow && (
            <div className="match-photo">
              <Button
                onClick={this.close}
                startIcon={<CancelIcon />}
                className="raffle-button close"
              >
                Close
              </Button>

              {!this.state.isMobile ? (
                <>
                  {!showVideo && (
                    <Button
                      onClick={this.handleTakePhotoClick}
                      startIcon={<AddAPhoto />}
                      className="raffle-button take"
                    >
                      Open Camera
                    </Button>
                  )}

                  {showVideo && (
                    <Button
                      startIcon={<Camera />}
                      onClick={this.captureImage}
                      className="raffle-button take"
                    >
                      Capture Image
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {!this.state.isCameraOpen && (
                    <Button
                      onClick={this.handleOpenCameraMobile}
                      startIcon={<AddAPhoto />}
                      className="raffle-button take"
                    >
                      Open Camera
                    </Button>
                  )}

                  {this.state.isCameraOpen && (
                    <Button
                      startIcon={<Camera />}
                      onClick={this.capturePhotoMobile}
                      className="raffle-button take"
                    >
                      Capture Image
                    </Button>
                  )}

                  {this.state.isCameraOpen && (
                    <Button
                      startIcon={<FlipCameraIosIcon />}
                      onClick={this.switchCameraMobile}
                      className="raffle-button take"
                      aria-label="Switch Camera"
                    >
                      {this.state.cameraFacingMode === 'environment'
                        ? 'Front'
                        : 'Back'}
                    </Button>
                  )}
                </>
              )}

              <Button
                onClick={this.acceptPhoto}
                disabled={!capturedImageURL}
                startIcon={<HowToRegIcon />}
                className="raffle-button accept"
              >
                Accept Picture
              </Button>
            </div>
          )}
        </div>
      </Paper>
    );
  };

  close = () => {
    this.setState(
      {
        search: '',
        isMatchPictureShow: false,
        selectedCustomer: null,
        capturedImageURL: null,
        newPhotos: [],
        isPhotoVerified: false,
        clickedRows: {},
      },
      () => {
        this.stopWebcam();
        this.stopCameraMobile();
        this.props.clearAllCustomers();
      }
    );
  };

  checkNext = () => {
    // Check conditions based on the current step
    const { activeStep, winner, amount, selectedCustomer, isPhotoVerified } =
      this.state;

    // Step 0: Check if there is a winner
    if (activeStep === 0) {
      return !winner; // Disable Next if no winner is selected (or any other condition for winner)
    }

    // Step 1: Check for winner and amount
    if (activeStep === 1) {
      return !(winner && amount); // Disable Next if either winner or amount is missing
    }

    // Step 2: Check for selectedCustomer and isPhotoVerified
    if (activeStep === 2) {
      return !(selectedCustomer && isPhotoVerified); // Disable Next if selectedCustomer is missing or isPhotoVerified is false
    }

    // Default case: Allow Next button to be enabled
    return false;
  };

  submitRaffle = () => {
    const { newPhotos } = this.state;
    const data = {
      userId: this.props.user.userId,
      locationId: this.props.user.selectedLocationId,
      customerId: this.state.selectedCustomer.customerId,
      name:
        this.state.selectedCustomer.firstName +
        ' ' +
        this.state.selectedCustomer.lastName,
      amount: this.state.amount,
      winnerNumber: this.state.winner,
    };

    const optimisticFiles = newPhotos?.localURL
      ? [
          {
            localURL: newPhotos.localURL,
            displayName: newPhotos.displayName,
            type: newPhotos.type,
          },
        ]
      : undefined;

    this.props.saveRaffle(
      data,
      (response) => {
        this.props.openFeedbackBar('success', 'Saved');
        this.props.setDisableToolBar(false);
        this.props.setCurrentPage(RAFFLE_PAGES.TABLE);

        // Request B — multipart photo, fire and forget
        if (response?.raffleId && newPhotos?.file) {
          const formData = new FormData();
          formData.append('raffleId', response.raffleId);
          formData.append('photo', newPhotos.file);
          this.props.uploadRafflePhoto(formData);
        }
      },
      (message) => {
        this.props.openFeedbackBar('error', message);
      },
      optimisticFiles
    );
  };

  handleNext = () => {
    if (this.state.activeStep === STEPPER_HEADINGS.length - 1) {
      this.submitRaffle();
    } else {
      this.setState({
        activeStep: this.state.activeStep + 1,
      });
    }
  };

  getTableHeaders = () => {
    let headers = [];
    headers.push(Layout.columnHead(null, null, null));
    RAFFLE_CUSTOMER_FIELDS.forEach((headCell) => {
      headers.push(Layout.columnHead(headCell.label, headCell.id));
    });

    return headers;
  };

  handleEditRow = (customer, rowIndex) => {
    this.setState({ selectedCustomer: customer }, () => {
      this.setState({ isMatchPictureShow: true }, () => {
        //this.startWebcam();
      });
    });

    this.setState({
      clickedRows: { [rowIndex]: true },
    });
  };

  handleMouseEnter = (index) =>
    this.setState({ hoveredRows: { [index]: true } });
  handleMouseLeave = () => this.setState({ hoveredRows: {} });

  tableRow = (customer, index) => {
    const isGreen =
      this.state.hoveredRows[index] || this.state.clickedRows[index];

    return (
      <React.Fragment>
        <TableCell style={{ marginLeft: '16px' }}>
          <IconButton
            size="small"
            onClick={() => this.handleEditRow(customer)}
            onMouseEnter={() => this.handleMouseEnter(index)} // Set hover state on mouse enter
            onMouseLeave={() => this.handleMouseLeave(index)} // Set hover state on mouse leave
            style={{
              backgroundColor: isGreen ? 'green' : 'transparent',
              color: isGreen ? 'white' : 'inherit',
            }}
          >
            <CheckCircleIcon />
          </IconButton>
        </TableCell>

        <TableCell className="table-cell-left">
          <img
            src={customer?.files[0]?.localURL}
            alt="Customer"
            style={{
              width: this.state.isMatchPictureShow ? '80px' : '300px',
              height: 'auto',
            }}
          />
        </TableCell>
        <TableCell className="table-cell-left">{customer?.firstName}</TableCell>
        <TableCell className="table-cell-left">{customer?.lastName}</TableCell>
      </React.Fragment>
    );
  };

  acceptPhoto = () => {
    this.setState({ isPhotoVerified: true }, () => {
      this.stopWebcam();
      this.stopCameraMobile();
    });
  };

  render() {
    let headerButtons = [
      {
        icon: <ArrowBack />,
        handler:
          this.state.activeStep === 0 ? this.handlePrevScreen : this.handleBack,
        title: this.state.activeStep !== 0 ? BACK : this.props.backTitle,
      },
    ];

    if (this.state.activeStep === 0) {
      headerButtons.push({
        title: DRAW_MACHINE,
        icon: <DrawIcon />,
        handler: this.handleDraw,
        disabled: this.state.isDrawing, // Disable if drawing is in progress
      });
    }

    if (this.state.activeStep === STEPPER_HEADINGS.length - 1) {
      headerButtons.push({
        icon: this.props.loadingData ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <SaveIcon />
        ),
        handler: this.submitRaffle,
        title: this.props.loadingData ? 'Saving...' : 'Save',
        disabled: this.props.loadingData || this.checkNext(),
      });
    }

    headerButtons.push({
      icon: <ArrowForward />,
      handler: this.handleNext,
      title: NEXT,
    });

    if (this.checkNext()) {
      headerButtons[headerButtons.length - 1].disabled = true;
    }

    if (this.state.activeStep !== STEPPER_HEADINGS.length - 1) {
      headerButtons = headerButtons.filter((button) => button.title !== 'Save');
    }

    return (
      <>
        <CustomToolbar
          title={RAFFLE}
          createTitle={this.props.createTitle}
          backTitle={this.props.backTitle}
          handleClose={this.handleBack}
          headerButtons={headerButtons}
        />
        <Stepper
          activeStep={this.state.activeStep}
          alternativeLabel
          className="raffle-stepper"
        >
          {STEPPER_HEADINGS.map((label, index) => (
            <Step className="step" key={label}>
              <StepButton onClick={() => this.setStep(index)}>
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>

        <div className="customer-stepper-align">
          {this.getStepContent(this.state.activeStep)}
          <div className="raffle-action-row">
            <Button
              disabled={this.state.isDrawing}
              className="customer-save-invoice-query-button"
              variant="outlined"
              startIcon={
                this.state.activeStep === 0 ? <DrawIcon /> : <ArrowBack />
              }
              onClick={
                this.state.activeStep === 0 ? this.handleDraw : this.handleBack
              }
            >
              {this.state.activeStep === 0 ? 'Draw Machine' : BACK}
            </Button>

            <Button
              className="customer-save-invoice-query-button"
              variant="contained"
              color="primary"
              startIcon={
                this.state.activeStep === STEPPER_HEADINGS.length - 1 ? (
                  this.props.loadingData ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                ) : (
                  <ArrowForward />
                )
              }
              onClick={this.handleNext}
              disabled={this.props.loadingData || this.checkNext()}
            >
              {this.state.activeStep === STEPPER_HEADINGS.length - 1
                ? this.props.loadingData
                  ? 'Saving...'
                  : 'Save'
                : NEXT}
            </Button>
          </div>
        </div>
      </>
    );
  }
}

export default ManageRaffleStepper;
